/* D2 — Systems 3+1 : contexte frontend des Systems souscrits par le tenant.
   Le serveur (systems_enabled + gating D1) reste la SEULE source de vérité :
   ce contexte ne sert qu'à l'affichage (hub, sélecteur, états "non activé"). */
import { createContext, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Briefcase, Building2, CandlestickChart, ClipboardCheck, Coins, Compass, Gauge, Handshake, Inbox, KanbanSquare, ListChecks, Scale, Scale3d, ShieldAlert, ShieldCheck, TrendingUp, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { usePersona } from "@/lib/persona";

export const ALL_SYSTEMS = ["deal", "financial_markets", "governance"];

/* D3 — navigation par System (mapping vérifié sur les routes/fonctions réelles,
   pas sur les noms). Les items respectent le filtrage plan/modules existant via
   leur clé `module`. AUCUNE page dupliquée, AUCUNE URL renommée. */
export const SYSTEM_DEFS = [
  {
    id: "deal",
    label: "Deal System",
    description: "Origination, deals, courtage et immobilier — le moteur transactionnel.",
    icon: Briefcase,
    landing: "/app/deal-system",
    accent: "#378ADD",
    pathRe: /^\/app\/(deal-system|deal-pipeline|deals|courtage|immobilier|origination|leads|negociation|sources-capital)/,
    nav: [
      { to: "/app/deal-system", label: "Deal", icon: Gauge, module: "deals" },
      { to: "/app/origination", label: "Origination", icon: Compass, module: "origination", min_depth: 1 },
      { to: "/app/leads", label: "Leads", icon: Inbox, module: "leads", min_depth: 1 },
      { to: "/app/negociation", label: "Négociations", icon: Scale, module: "negociation", min_depth: 2 },
      { to: "/app/deal-pipeline", label: "Pipeline", icon: KanbanSquare, module: "deals", min_depth: 3 },
      { to: "/app/deals", label: "Deals", icon: Handshake, module: "deals", min_depth: 3 },
      { to: "/app/decisions", label: "Décisions", icon: Scale3d, min_depth: 4 },
      { to: "/app/sources-capital", label: "Contreparties capital", icon: Coins, module: "origination_capital", min_depth: 5 },
      { to: "/app/courtage", label: "Courtage", icon: Briefcase, module: "courtage", min_depth: 5 },
      { to: "/app/immobilier", label: "Immobilier", icon: Building2, module: "immobilier", min_depth: 5 },
    ],
  },
  {
    id: "financial_markets",
    label: "Financial Markets",
    description: "Vue d'ensemble, screener multi-marchés, instruments et trading IA — l'intelligence de marché.",
    icon: CandlestickChart,
    landing: "/app/financial-overview",
    accent: "#1D9E75",
    pathRe: /^\/app\/(brvm|financial-overview)/,
    nav: [
      { to: "/app/financial-overview", label: "Vue d'ensemble", icon: Gauge, min_depth: 1 },
      { to: "/app/radar?system=financial_markets", label: "Market Awareness", icon: TrendingUp, min_depth: 1 },
      { to: "/app/org-model?perspective=fm&system=financial_markets", label: "Exposition marché", icon: Scale, min_depth: 2 },
      { to: "/app/alertes?system=financial_markets", label: "Market Intelligence", icon: Inbox, module: "alertes", min_depth: 3 },
      { to: "/app/decisions?system=financial_markets", label: "Décisions financières", icon: Scale3d, min_depth: 4 },
      { to: "/app/brvm", label: "Market Screener", icon: TrendingUp, module: "brvm", tab: "screener", min_depth: 5 },
      { to: "/app/brvm?tab=trading", label: "Trading & Portfolio", icon: CandlestickChart, module: "brvm", tab: "trading", min_depth: 5 },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    description: "Risques, contrôles et conformité — le cadre GRC de l'organisation.",
    icon: ShieldCheck,
    landing: "/app/gouvernance",
    accent: "#7F77DD",
    pathRe: /^\/app\/(gouvernance|risques|audits|controles|fournisseurs|processus)/,
    nav: [
      { to: "/app/gouvernance", label: "Gouvernance & Conformité", icon: ClipboardCheck, module: "gouvernance" },
      { to: "/app/processus", label: "Cartographie des processus", icon: ClipboardCheck, module: "gouvernance" },
      { to: "/app/risques", label: "Risques", icon: ShieldAlert, module: "risques", min_depth: 2 },
      { to: "/app/audits", label: "Audits", icon: ClipboardCheck, module: "audits", min_depth: 3 },
      { to: "/app/controles", label: "Contrôles", icon: ListChecks, module: "controles" },
      { to: "/app/fournisseurs", label: "Fournisseurs", icon: Truck, module: "fournisseurs" },
    ],
  },
];

/* Contexte de navigation dérivé de l'URL (stateless : deep links, refresh,
   back/forward fonctionnent naturellement). null = hors System (Platform).
   Continuité de shell : le paramètre ?system=<id> (porté par les items de nav
   des Systems vers les pages Platform transverses) prime sur le préfixe de
   chemin — l'utilisateur reste dans le contexte visuel de son System. */
export function systemForPath(pathname, search = "") {
  const q = pathname.includes("?") ? pathname : `${pathname}${search || ""}`;
  const m = /[?&]system=([a-z_]+)/.exec(q);
  if (m && ALL_SYSTEMS.includes(m[1])) return m[1];
  const cleanPath = pathname.split("?")[0];
  const def = SYSTEM_DEFS.find((d) => d.pathRe.test(cleanPath));
  return def ? def.id : null;
}

/* W17-B4 — landing unifié : FM Overview (L1) convient à toutes les profondeurs. */
export function systemLanding(def, depth) {
  if (def?.id === "financial_markets" && (depth?.financial_markets ?? 5) < 5) {
    return "/app/financial-overview";
  }
  return def?.landing;
}

const SystemsContext = createContext({ systems: ALL_SYSTEMS, configured: false, loading: false, depth: {} });

export function SystemsProvider({ children }) {
  const [state, setState] = useState({ systems: ALL_SYSTEMS, configured: false, loading: true, depth: {} });

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .get("/workspace/systems")
        .then((r) => {
          if (!cancelled)
            setState({
              systems: r.data?.systems || ALL_SYSTEMS,
              configured: !!r.data?.configured,
              depth: r.data?.depth || {},
              loading: false,
            });
        })
        .catch(() => {
          // Affichage fail-open ; les données restent protégées par le gating serveur.
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
        });
    load();
    window.addEventListener("noam:org-updated", load);
    return () => {
      cancelled = true;
      window.removeEventListener("noam:org-updated", load);
    };
  }, []);

  return <SystemsContext.Provider value={state}>{children}</SystemsContext.Provider>;
}

export const useSystems = () => useContext(SystemsContext);

/* Atterrissage post-login : 1 seul System souscrit -> redirection directe dedans ;
   sinon hub NOAM. Le hub reste accessible via le logo. */
export function HubIndexRedirect() {
  const { systems, loading, depth } = useSystems();
  const { manifest, loading: personaLoading } = usePersona();
  // AGENTIC V1 — le mode agentic atterrit sur « Que voulez-vous accomplir ? »
  const [agentic, setAgentic] = useState(null);
  useEffect(() => {
    api.get("/workspace/agentic/mode")
      .then((r) => setAgentic(r.data?.mode === "agentic"))
      .catch(() => setAgentic(false));
  }, []);
  if (loading || personaLoading || agentic === null) return null;
  if (agentic) return <Navigate to="/app/noam" replace />;
  // W17-B5 — profils débutant / opérationnel : atterrir sur « Aujourd'hui »
  // (attention → pourquoi → recommandation → action), pas sur la carte du produit.
  if (["debutant", "operationnel"].includes(manifest?.ui_level)) {
    return <Navigate to="/app/aujourdhui" replace />;
  }
  if (systems.length === 1) {
    const def = SYSTEM_DEFS.find((d) => d.id === systems[0]);
    if (def) return <Navigate to={systemLanding(def, depth)} replace />;
  }
  return <Navigate to="/app/hub" replace />;
}
